import React, { useState } from 'react';
import { AutoComplete } from '@spark';
import { StatusDot } from '@/features/tasks/components/HomePage/task/TaskStatusSummary';
import {
    formatStatusFilterLabel,
    STATUS_FILTER_OPTIONS,
} from '@/features/tasks/utils/statusFilter';

/**
 * Reusable status filter select (Spark Auto-complete, searchable multi-select).
 *
 * Per Spark-DS guidance, a long list uses Auto-complete: users type to filter the
 * list and pick multiple options (shown as tags).
 *
 * Same contract: value is "All"/allValue or an array of statuses; onChange receives
 * an array, or allValue when the selection is cleared.
 *
 * Open state is driven via AutoComplete `onOpenChange` (not a wrapRef mousedown
 * handler) so portaled panel clicks stay inside and multi-select works on scroll.
 */
const StatusFilterSelect = ({
    value,
    onChange,
    allValue = 'All',
    placeholder = 'Filter By Status',
    className = 'status-filter-select',
    statusCounts = {},
}) => {
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState('');

    const selected = React.useMemo(() => {
        if (!value || value === allValue) return [];
        return Array.isArray(value) ? value : [value];
    }, [value, allValue]);

    const toggle = (status) => {
        const next = selected.includes(status)
            ? selected.filter((v) => v !== status)
            : [...selected, status];
        onChange(next.length === 0 ? allValue : next);
    };

    const normalizedQuery = query.trim().toLowerCase();
    const filteredStatuses = normalizedQuery
        ? STATUS_FILTER_OPTIONS.filter((status) => status.toLowerCase().includes(normalizedQuery))
        : STATUS_FILTER_OPTIONS;

    const items = filteredStatuses.map((status) => ({
        label: formatStatusFilterLabel(status, statusCounts[status] ?? 0),
        prefix: <StatusDot status={status} />,
        checked: selected.includes(status),
        onClick: () => toggle(status),
    }));

    return (
        <div
            className={className}
            onClick={() => setOpen(true)}
            onFocus={() => setOpen(true)}
        >
            <AutoComplete
                type="multi-select"
                size="medium"
                placeholder={placeholder}
                open={open}
                showChevron
                onOpenChange={(next) => {
                    setOpen(next);
                    if (!next) setQuery('');
                }}
                value={query}
                tags={selected}
                items={items}
                onChange={(e) => setQuery(e.target.value)}
                onTagRemove={(_tag, index) => toggle(selected[index])}
            />
        </div>
    );
};

export default StatusFilterSelect;
